import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AddTokenRegistrationPayload, IRegistration, RegistrationCustomAnswer } from './interfaces'
import { IEvent, PriceCategory } from '../events/interfaces'
import { IDiscountCode } from '../paymentData/index'
import { DiscountCodeModes, IProgram } from '../program/interfaces'
import { setEvents } from '../events/events'

const cleanPriorities = (registrations: IRegistration[]) =>
  registrations
    .slice()
    .sort((a: IRegistration, b: IRegistration) => a.priority - b.priority)
    .map((registration: IRegistration, index: number) => ({
      ...registration,
      priority: index,
    }))

export const calculateCustomPrice = (registration: IRegistration, discountCodes: IDiscountCode[], program: IProgram) => {
  let totalDiscount = 0
  for (const dc of discountCodes) {
    if ((dc.events && dc.events.includes(registration.event?.event_id)) || program.discountcode_mode == DiscountCodeModes.DISCOUNTCODE_PROGRAM) {
      totalDiscount += dc.discount
    }
  }
  if (totalDiscount > 0)
    return registration.price * (1 - Math.min(100, totalDiscount) / 100)
  else
    return -1
}

export const initialState = {
  list: [] as IRegistration[],
}

const registrationsSlice = createSlice({
  name: 'registrations',
  initialState,
  reducers: {
    addRegistration(state, action: PayloadAction<IEvent>) {
      const eventIndex = state.list.findIndex(
        (registration) => registration.event?.event_id === action.payload.event_id
      )
      // If the event exists, remove it from the list
      if (eventIndex !== -1) {
        state.list.splice(eventIndex, 1)
      }
      state.list.push({
        event: action.payload,
        price: action.payload.price || 0,
        priority: state.list.length,
        numberOfAdults: 1,
        numberOfChildren: 0,
        priceCategory: (action.payload.price === 0 && action.payload.hide_price) ? 'NONE' : '',
        groupCode: '',
        specialNotes: '',
        customAnswers: [],
        customPrice: action.payload.custom_price || -1,
        discount: 0,
        discountCodes: [] as IDiscountCode[],
        event_name: action.payload.name,
        event_id: action.payload.event_id,
        event_rid: action.payload.relative_id,
      })
    },
    addTokenRegistration(state, action: PayloadAction<AddTokenRegistrationPayload>) {
      const eventIndex = state.list.findIndex(
        (registration) => (registration.event_id ?? registration.event?.event_id) === action.payload.event_id
      )
      // If the event exists, remove it from the list
      if (eventIndex !== -1) {
        state.list.splice(eventIndex, 1)
      }
      state.list.push({
        event: action.payload.event,
        price: action.payload.price || 0,
        priority: action.payload.priority - 1,
        priceCategory: action.payload.price_category
          ? action.payload.price_category.name
          : '',
        groupCode: action.payload.group_code,
        customAnswers: action.payload.custom_answers.map((raw: any) => ({
          id: raw.question_id,
          answer: raw.answer_text,
        })),
        specialNotes: action.payload.special_notes,
        numberOfAdults: action.payload.number_of_adults,
        numberOfChildren: action.payload.number_of_children,
        customPrice: action.payload.custom_price || -1,
        discount: 0,
        discountCodes: [] as IDiscountCode[],
        loadedFromBackend: action.payload.loadedFromBackend || false,
        event_name: action.payload.event_name,
        event_id: action.payload.event_id,
        event_rid: action.payload.event_rid,
      })
    },
    fixRegistrationPriorities(state, action: PayloadAction<any>) {
      const sortedList = state.list.sort((a, b) => {
        if (a.loadedFromBackend && !b.loadedFromBackend) return -1;
        if (!a.loadedFromBackend && b.loadedFromBackend) return 1;
        return a.priority - b.priority; // Sort by priority within each group
      });

      // Sequentially assign new priorities starting from 1
      state.list = sortedList.map((registration, index) => ({
        ...registration,
        priority: index,
      }));
    },
    removeRegistration(state, action: PayloadAction<number>) {
      const remainingRegs = state.list.filter(
        (reg: IRegistration) => reg.event?.event_id !== action.payload
      )
      remainingRegs.map((reg: IRegistration) => (reg.priority = remainingRegs.indexOf(reg) + 1))
      state.list = cleanPriorities(remainingRegs)
    },
    removeAllRegistrations(state) {
      state.list = []
    },
    updateRegistration(state, action) {
      const { eventId, ...update } = action.payload
      const registrationIndex = state.list.findIndex(
        (registration: IRegistration) => registration.event?.event_id === eventId
      )
      state.list[registrationIndex] = {
        ...state.list[registrationIndex],
        ...update,
      }

      // update the as well price if the price category change
      if ('priceCategory' in update) {
        state.list[registrationIndex].price =
          (state.list[registrationIndex].priceCategory
            ? (state.list[registrationIndex].event.price_categories.find(
              (category: PriceCategory) =>
                category.name === state.list[registrationIndex].priceCategory
            ) as PriceCategory).price
            : state.list[registrationIndex].event.price) || 0
      }
    },
    updateRegistrationCustomAnswer(
      state,
      action: PayloadAction<{
        eventId: number
        answer: RegistrationCustomAnswer
      }>
    ) {
      const { eventId, answer } = action.payload
      const registrationIndex = state.list.findIndex(
        (reg: IRegistration) => reg.event?.event_id === eventId
      )
      const answerIndex = state.list[registrationIndex].customAnswers.findIndex(
        //TODO: If ans ins a CustomAnswer, this is a type error
        (ans: any) => ans.id === answer.id
      )
      try {
        state.list[registrationIndex].customAnswers[answerIndex].answer =
          answer.answer
      } catch (e) {
        state.list[registrationIndex].customAnswers.push(answer)
      }
    },
    updatePriority(
      state,
      action: PayloadAction<{ eventId: number; dPriority: number }>
    ) {
      const { eventId, dPriority } = action.payload
      const registrationIndex = state.list.findIndex(
        (registration: IRegistration) => registration.event?.event_id === eventId
      )
      const targetPriority = state.list[registrationIndex].priority - dPriority

      if (targetPriority === -1) {
        // Is first wants to be last
        state.list = state.list.map((registration: IRegistration, key: number) => ({
          ...registration,
          priority:
            key === registrationIndex
              ? state.list.length - 1
              : registration.priority - 1,
        }))
      } else if (targetPriority === state.list.length) {
        // Is last wants to be first
        state.list = state.list.map((registration: IRegistration, key: number) => ({
          ...registration,
          priority: key === registrationIndex ? 0 : registration.priority + 1,
        }))
      } else {
        // Is somewhere, wants to be somewhere else
        const targetRegistrationIndex = state.list.findIndex(
          (registration: IRegistration) =>
            registration.priority === targetPriority
        )

        const oldPrio = state.list[registrationIndex].priority
        state.list[registrationIndex].priority = targetPriority
        state.list[targetRegistrationIndex].priority = oldPrio
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setEvents, (state, action) => {
      // Remove registrations for events that no longer exist, this runs after setEvents was called
      if (action.payload.length > 0) {      
        state.list = state.list.filter((reg) => {
          const {event} = reg
          if (!event) {
            // Keep backend-loaded registrations whose events are hidden (hide_past_events)
            return !!reg.loadedFromBackend;
          }
          return action.payload.some((e: IEvent) => e.event_id === event.event_id) || reg.loadedFromBackend;
        });
      }
    });
  },
})
export default registrationsSlice.reducer
export const {
  addRegistration,
  addTokenRegistration,
  fixRegistrationPriorities,
  removeRegistration,
  removeAllRegistrations,
  updateRegistration,
  updateRegistrationCustomAnswer,
  updatePriority,
} = registrationsSlice.actions
